import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";

import { authAPI } from "../services/api"; // Import the authAPI
import {
  isTokenExpired,
  clearAuthData as utilsClearAuthData,
} from "../utils/authUtils";

// Define the shape of the AuthContext
interface AuthContextType {
  user: any | null;
  role: string | null;
  clientId: string | null;
  loading: boolean;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
}

// Create the AuthContext with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  role: null,
  clientId: null,
  loading: true,
  logout: () => {},
  checkAuth: async () => false,
});

// AuthContext provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<any | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  // Use ref to prevent multiple checks
  const authChecked = useRef(false);

  // Helper to clear auth data
  const clearAuthData = () => {
    setUser(null);
    setRole(null);
    setClientId(null);
    utilsClearAuthData();
  };

  const checkAuth = async (): Promise<boolean> => {
    // Don't check again if we've already done it
    if (authChecked.current && user) {
      return true;
    }

    setLoading(true);

    // Check if token exists in localStorage
    const token = localStorage.getItem("token");
    const storedRole = localStorage.getItem("role");
    const storedClientId = localStorage.getItem("clientId");
    const userId = localStorage.getItem("userId");
    const clientName = localStorage.getItem("clientName");

    if (token && userId) {
      // Check if token is expired
      if (isTokenExpired(token)) {
        console.log("Token is expired, logging out");
        clearAuthData();
        setLoading(false);

        return false;
      }

      // For simplicity, trust the stored values rather than making a verification call
      setUser({
        id: userId,
        role: storedRole,
        clientId: storedClientId,
        clientName: clientName,
      });

      setRole(storedRole);
      setClientId(storedClientId);
      setLoading(false);

      // Mark that we've checked auth
      authChecked.current = true;

      return true;
    } else {
      // No token, user is not authenticated
      clearAuthData();
      setLoading(false);

      return false;
    }
  };

  useEffect(() => {
    // Skip the check if we've already authenticated
    if (authChecked.current) {
      setLoading(false);

      return;
    }

    checkAuth();

    // Clean up function
    return () => {
      // No cleanup needed
    };
  }, []);

  const logout = async () => {
    try {
      // Call the backend logout endpoint via the API service
      await authAPI.logout();
    } catch (error) {
      console.error("Error during logout:", error);
    } finally {
      clearAuthData();
      authChecked.current = false;

      // Redirect to login page
      navigate("/login");
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, role, clientId, loading, logout, checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the AuthContext
export const useAuth = () => useContext(AuthContext);
